// MemberDetailPage.js
import React, { useEffect, useState } from 'react';
import { Button, Modal, Spinner, Image } from 'react-bootstrap';
import { Files } from 'react-bootstrap-icons';
import { useParams, useLocation } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import { useTranslation } from 'react-i18next';

import SponsoredBills from './SponsoredBills';
import APIMemberVotePull from './APIMemberVotePull';
import APIMemberVotePullModal from './APIMemberVotePullModal';

import useCollections, { CollectionModal, CollectionIcon } from '../../hooks/useCollections';
import useApi2Resource from '../../hooks/useApi2Resource';
import { fetchMemberRecord, sourceFor } from '../../utils/officials';

import './MemberDetailPage.css';
import MemberFeedItem from './MemberFeedItem';
import useSponsoredBills from '../../hooks/useSponsoredBills';
import useMember from '../../hooks/useMember';
import {
  MemberBillsList,
  MemberCommitteesPanel,
  MemberCompaniesPanel,
  MemberFilingsPanel,
  MemberMoneyPanel,
  MemberRecordSummary,
  MemberTradesPanel,
  MemberVotesList,
  ScrollableTabStrip,
  useRecordTabLabels,
} from './MemberRecord';

const SCHEMA = 'united_states_of_america';
const BILLS_TABLE = 'congressional_bills';

const VOTES_TAB = 'votes';
const BILLS_TAB = 'bills';
const COSPONSORED_TAB = 'cosponsored';

// Tabs that come only from api2's /record. Offered from the first paint, so
// the tab strip never changes shape under the reader: while the record loads,
// these panels show a spinner. Only if the record cannot be read are they
// withdrawn, leaving Votes and Bills.
//
// Corporate Ties ('companies') sits last: it is the one tab that looks outward
// from the member to the companies around them, so it closes the strip rather
// than interrupting the member's own record.
const RECORD_ONLY_TABS = ['money', 'trades', 'committees', 'filings', 'companies'];

const SENATE = 'Senate';
const HOUSE = 'House of Representatives';

const openCapacitorSite = async (link) => {
  await Browser.open({ url: link });
};

const getOrdinalSuffix = (number) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const v = number % 100;
  return number + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

// The route already knows the chamber - /us/senator/:id vs
// /us/representative/:id - so it is read from the path rather than from
// location.state, which is absent on any link the app did not navigate to
// itself. Matching on the path segment rather than a substring keeps
// /us/senators (the list route) from ever being mistaken for a detail page.
//
// Values must stay exactly 'Senate' and 'House of Representatives': they are
// what the API publishes on terms[].chamber and what
// getMemberConfigByChamber matches on.
const chamberFromPath = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);
  return segments[1] === 'senator' ? SENATE : HOUSE;
};

// Two upstreams reach this page with different address shapes:
//   api.govglance.org/members/  -> address_information object with
//                                  officeAddress / city / district / phoneNumber
//   api2.govglance.org          -> one flat office_address string plus a
//                                  separate phone field
// Reading the object shape against a string made every field miss and printed
// the "not available" fallback three times over, so both are accepted here.
const normalizeAddress = (address, phoneFallback) => {
  if (!address) {
    return { lines: [], phone: phoneFallback || null };
  }

  if (typeof address === 'string') {
    return { lines: [address.replace(/\s+/g, ' ').trim()], phone: phoneFallback || null };
  }

  // Object shape: join only the parts that are actually present, so a missing
  // city no longer drags a placeholder into the middle of the line.
  const streetLine = [address.officeAddress, address.city, address.district]
    .filter(Boolean)
    .join(', ');

  return {
    lines: streetLine ? [streetLine] : [],
    phone: address.phoneNumber || phoneFallback || null,
  };
};

/**
 * MemberDetailPage
 *
 * ─── Where each part comes from ───────────────────────────────────────────────
 *
 * The profile header is unchanged: useMember, seeded from the list page.
 *
 * Votes and bills come from whichever source utils/officials says
 * (MEMBER_RECORD_SOURCES). While it says api1, they are the same components
 * as before. When it says api2 -- once scripts/compare-member-records.mjs has
 * confirmed api2 matches -- the api2 lists render, and api1's render in their
 * place if api2 fails for this member. Senators always get votes from api2,
 * because api1 has no Senate roll calls; that is what gives them a Votes tab
 * at last.
 *
 * Everything else is api2's /officials/{bioguide}/record, read once per page:
 * the count lines in the profile, and the Campaign $, Stock trades,
 * Committees, Disclosures and Corporate Ties tabs. Every tab is there from the
 * first paint, so the strip never shifts when the record arrives. If
 * it cannot be read, the record tabs and count lines are withdrawn and the page is
 * what it was.
 *
 * Only the active tab's panel mounts, so a tab's lists are never requested
 * until someone opens it (the record itself is one request for all of them).
 *
 * The page sits directly on the theme's page background. #feedDisplay carries
 * a card surface of its own from the shared feed styles; MemberDetailPage.css
 * switches it off for this page, so no box ends just above the footer.
 */
function MemberDetailPage() {
  const { t } = useTranslation('common');
  const { bioGuideId } = useParams();
  const location = useLocation();

  // Authoritative for tab availability and for the collection URL, both of
  // which used to break on a cold load: an undefined chamber offered senators
  // a Votes tab that could never fill, and sent their saved item to a
  // /representative/ URL that did not match the one saved from the list page.
  const routeChamber = chamberFromPath(location.pathname);

  // What the list pages hand over on click. Null on a fresh navigation.
  const seed = location.state?.name
    ? {
        name: location.state.name,
        address: location.state.address,
        phone: location.state.phone,
        district: location.state.district,
        webURL: location.state.webURL,
        imageSource: location.state.imageSource,
        chamber: location.state.chamber || routeChamber,
        startYear: location.state.startYear,
        stateName: location.state.stateName,
      }
    : null;

  const { member, loading: memberLoading, error: memberError } = useMember(
    bioGuideId,
    seed,
    routeChamber
  );

  const name = member?.name;
  const address = member?.address;
  const phone = member?.phone;
  const district = member?.district;
  const webURL = member?.webURL;
  const imageSource = member?.imageSource;
  const startYear = member?.startYear;
  const stateName = member?.stateName;
  const chamber = routeChamber;
  const isSenator = chamber === SENATE;

  const [showModal, setShowModal] = useState(false);
  const [showModal2, setShowModal2] = useState(false);
  // The full list of the member's stock trades, from the Trades tab's View
  // More, the way Votes and Bills open theirs.
  const [showTradesModal, setShowTradesModal] = useState(false);
  const [bioGuideIdState, setBioGuideIdState] = useState(null);
  // Which bills list View More opens: the tab it was pressed on.
  const [modalBillsRole, setModalBillsRole] = useState('sponsor');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [activeTab, setActiveTab] = useState(VOTES_TAB);

  const votesSource = sourceFor('votes', isSenator);
  const billsSource = sourceFor('bills', isSenator);

  // The api2 record: glance tiles and the record-only tabs.
  const record = useApi2Resource(() => fetchMemberRecord(bioGuideId), [bioGuideId]);
  const recordData = record.data || null;

  // The shared labels, with the companies tab renamed. The key stays
  // 'companies' so the panel, the summary line that opens it, and anything
  // else keyed on it are untouched.
  const baseTabLabels = useRecordTabLabels();
  const tabLabels = {
    ...baseTabLabels,
    companies: t('common:corporateTies', 'Corporate Ties'),
  };

  const recordFailed = Boolean(record.error);
  const tabs = [VOTES_TAB, BILLS_TAB, COSPONSORED_TAB, ...(recordFailed ? [] : RECORD_ONLY_TABS)];
  const currentTab = tabs.includes(activeTab) ? activeTab : VOTES_TAB;

  // A new member is a new page: back to the first tab.
  useEffect(() => {
    setActiveTab(VOTES_TAB);
  }, [bioGuideId]);

  // Both panels now share one viewport slot, so the list stays short enough
  // that the tab strip and the View More button remain visible without
  // scrolling - tighter on mobile, where the profile block eats more height.
  const itemLimit = isDesktop ? 6 : 2;

  // Sponsored bills from api1, already joined with their detail records. The
  // api1 list when it is the source, and the fallback when api2 is.
  const { bills, loading, error } = useSponsoredBills(bioGuideId, itemLimit);

  const contact = normalizeAddress(address, phone);

  // Translated fallback, shown once when nothing at all is known rather than
  // once per missing field.
  const contactFallback = t('common:officeAddressNotAvailable', 'Office address not available');

  // Track window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Use the collection hook
  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    openCollectionModal,
    closeModal,
    handleSave,
    isStorageAvailable
  } = useCollections();

  // The member profile's identity in the collection is its url, so the save
  // date can stay a fresh timestamp without unsettling the saved/unsaved icon.
  // feedName/branch/topic stay English per stored-data convention
  const memberUrl = `https://news.govglance.org/us/${isSenator ? 'senator' : 'representative'}/${bioGuideId}`;

  const buildMemberCollectionItem = () => ({
    title: name,
    url: memberUrl,
    docLink: webURL || '',
    date: new Date().toISOString(), // Current date as raw ISO string
    feedName: `U.S. ${chamber}`,
    branch: 'Legislative',
    topic: 'Members'
  });

  // Handle adding member to collection
  const handleAddMemberToCollection = () => {
    openCollectionModal(buildMemberCollectionItem());
  };

  const handleViewMoreClick = (role = 'sponsor') => {
    setBioGuideIdState(bioGuideId);
    setModalBillsRole(role);
    setShowModal(true);
  };

  // A summary line opens its tab, and brings the tab strip into view.
  const openTab = (tab) => {
    setActiveTab(tab);
    const strip = document.getElementById('member-record-tabs');
    if (strip) {
      const top = strip.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  // Subtitle parts are joined rather than concatenated with fixed separators:
  // api2 publishes no term_start for senators, which previously left a trailing
  // "Indiana Senate - " with nothing after the dash.
  const desktopSubtitle = [stateName, chamber, startYear].filter(Boolean).join(' · ');
  const mobileSubtitle = [stateName, startYear].filter(Boolean).join(' · ');

  const renderContactBlock = (modifier) => {
    const hasAnything = contact.lines.length > 0 || contact.phone || webURL;

    return (
      <div className={`member-contact member-contact--${modifier}`}>
        {!hasAnything && <span>{contactFallback}</span>}

        {contact.lines.map((line) => (
          <React.Fragment key={line}>
            <span>{line}</span>
            <br />
          </React.Fragment>
        ))}

        {contact.phone && (
          <>
            <span>{contact.phone}</span>
            <br />
          </>
        )}

        {webURL && (
          <span
            onClick={(e) => {
              e.preventDefault();
              openCapacitorSite(webURL);
            }}
            className="member-link"
          >
            {webURL}
          </span>
        )}
      </div>
    );
  };


  // api1's sponsored bills, as before.
  const renderSponsoredBills = () => {
    if (loading) {
      return <Spinner id='spinner' animation="grow" variant="light" />;
    }

    if (error) {
      return (
        <p className="member-detail-text">
          {t('common:errorPrefix', 'Error: {{message}}', { message: error.message })}
        </p>
      );
    }

    if (!bills || bills.length === 0) {
      return <p className="member-detail-text">{t('common:noDataAvailable', 'No data available.')}</p>;
    }

    return (
      <div className="member-feed-grid">
        {bills.map((bill) => (
          <MemberFeedItem
            key={bill.details?.id ?? bill.id}
            item={bill.details}
            schema={SCHEMA}
            table={BILLS_TABLE}
            feedName="Bill Updates"
            branch="Legislative"
            topic="Sponsored Bills"
            buttonName={t('common:bill', 'Bill')}
            variant="default"
          />
        ))}
      </div>
    );
  };

  const renderTabs = () => (
    <ScrollableTabStrip
      id="member-record-tabs"
      className={`member-tabs member-tabs--scroll ${isDesktop ? 'member-tabs--center' : ''}`}
      ariaLabel={t('common:memberRecord', 'Record')}
      activeKey={currentTab}
    >
      {tabs.map((tab) => (
        <button
          key={tab}
          type="button"
          role="tab"
          id={`member-tab-${tab}`}
          aria-selected={currentTab === tab}
          aria-controls={`member-panel-${tab}`}
          className={`member-tab ${currentTab === tab ? 'member-tab--active' : ''}`}
          onClick={() => setActiveTab(tab)}
        >
          {tabLabels[tab]}
        </button>
      ))}
    </ScrollableTabStrip>
  );

  const viewMoreButton = (onClick) => (
    <div className={isDesktop ? 'text-center' : ''}>
      <Button id='repsViewMore' onClick={onClick}>
        <Files size={20} /> {t('common:viewMore', 'View More')}
      </Button>
    </div>
  );

  // A record tab opened before the record has arrived.
  const recordPending = (
    <div className="member-record-pending">
      <Spinner animation="border" size="sm" />
    </div>
  );

  const renderPanel = () => {
    if (RECORD_ONLY_TABS.includes(currentTab) && !recordData) return recordPending;

    switch (currentTab) {
      case VOTES_TAB:
        return (
          <>
            {votesSource === 'api2' ? (
              <MemberVotesList
                bioguideId={bioGuideId}
                limit={itemLimit}
                fallback={isSenator ? null : <APIMemberVotePull bio_guide_id={bioGuideId} limit={itemLimit} />}
              />
            ) : (
              <APIMemberVotePull bio_guide_id={bioGuideId} limit={itemLimit} />
            )}
            {viewMoreButton(() => setShowModal2(true))}
          </>
        );
      case BILLS_TAB:
        return (
          <>
            {billsSource === 'api2' ? (
              <MemberBillsList bioguideId={bioGuideId} limit={itemLimit} fallback={renderSponsoredBills()} />
            ) : (
              renderSponsoredBills()
            )}
            {viewMoreButton(() => handleViewMoreClick('sponsor'))}
          </>
        );
      case COSPONSORED_TAB:
        // api2 only: api1 lists sponsored bills alone.
        return (
          <>
            <MemberBillsList bioguideId={bioGuideId} limit={itemLimit} role="cosponsor" />
            {viewMoreButton(() => handleViewMoreClick('cosponsor'))}
          </>
        );
      case 'money':
        return <MemberMoneyPanel record={recordData} />;
      case 'trades': {
        // Like Votes and Bills: the newest few in the tab, and View More for
        // every trade on file in a modal. The button only appears when the
        // record holds more trades than the tab is showing.
        const tradeCount = recordData?.stock_trades?.transactions;
        return (
          <>
            <MemberTradesPanel bioguideId={bioGuideId} record={recordData} limit={itemLimit} />
            {typeof tradeCount === 'number' && tradeCount > itemLimit &&
              viewMoreButton(() => setShowTradesModal(true))}
          </>
        );
      }
      case 'committees':
        return <MemberCommitteesPanel record={recordData} />;
      case 'filings':
        return <MemberFilingsPanel record={recordData} />;
      case 'companies':
        return <MemberCompaniesPanel bioguideId={bioGuideId} record={recordData} />;
      default:
        return null;
    }
  };

  // The record's counts as lines in the profile, each opening its tab.
  const renderSummary = () =>
    recordFailed ? null : (
      <MemberRecordSummary record={recordData} loading={record.loading} onSelect={openTab} />
    );

  // A cold load has to wait on the roster request, so the header shows a
  // spinner rather than the shape of a profile with every field blank - which
  // otherwise looks identical to a failed load.
  const renderProfile = () => {
    if (memberLoading) {
      return (
        <div className="member-profile-container">
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      );
    }

    if (memberError || !member) {
      return (
        <div className="member-profile-container">
          <p className="member-detail-text">
            {t('common:memberNotFound', 'This member could not be found.')}
          </p>
        </div>
      );
    }

    if (isDesktop) {
      return (
        <div className="member-profile-container">
          <div className="member-profile-image member-profile-image--desktop">
            {imageSource && <Image id='profileImage-image' src={imageSource} />}
          </div>

          <div className="member-profile-info member-profile-info--desktop">
            <div className="member-name member-name--desktop">
              <span>{name}</span>
            </div>
            {desktopSubtitle && (
              <div className="member-subtitle">
                <span>{desktopSubtitle}</span>
              </div>
            )}
            {district !== "" && district != null && (
              <div className="member-subtitle">
                {t('common:nthDistrict', '{{ordinal}} District', { ordinal: getOrdinalSuffix(district) })}
              </div>
            )}
            {renderContactBlock('desktop')}
            {renderSummary()}
          </div>
        </div>
      );
    }

    return (
      <div className="member-profile-container">
        <div className="member-profile-image member-profile-image--mobile">
          {imageSource && <Image id='profileImage-image' src={imageSource} />}
        </div>

        <div className="member-profile-info member-profile-info--mobile">
          <div className="member-name member-name--mobile">
            <span>{name}</span>
          </div>
          {chamber && (
            <div className="member-subtitle">
              <span>U.S. {chamber}</span>
            </div>
          )}
          {district !== "" && district != null ? (
            <div className="member-subtitle">
              {t('common:nthDistrict', '{{ordinal}} District', { ordinal: getOrdinalSuffix(district) })}
              {mobileSubtitle ? ` · ${mobileSubtitle}` : ''}
            </div>
          ) : (
            mobileSubtitle && <div className="member-subtitle">{mobileSubtitle}</div>
          )}
          {renderContactBlock('mobile')}
          {renderSummary()}
        
        </div>
      </div>
    );
  };

  return (
    <div id='feedDisplay' className="member-detail-page">
      <div className="member-page-content">
        {renderProfile()}

        {/* One tab strip over the whole record. Only the active panel
            mounts, so a list is never requested until its tab is opened. */}
        <div className="member-section member-section--tabbed">
          {renderTabs()}

          <div
            className="member-tab-panel"
            role="tabpanel"
            id={`member-panel-${currentTab}`}
            aria-labelledby={`member-tab-${currentTab}`}
          >
            {renderPanel()}
          </div>
        </div>
      </div>

<br></br>  <br></br>  <br></br>

      <Modal id='fullBillModal' size="xl" aria-labelledby="contained-modal-title-vcenter" show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header id='billModalHeader' closeButton>
          <h3>
            {modalBillsRole === 'cosponsor'
              ? t('common:memberCosponsoredBillsTitle', '{{name}} Cosponsored Bills', { name })
              : t('common:memberSponsoredBillsTitle', '{{name}} Sponsored Bills', { name })}
          </h3>
        </Modal.Header>
        {billsSource === 'api2' || modalBillsRole === 'cosponsor' ? (
          <div className="member-modal-list">
            <MemberBillsList
              bioguideId={bioGuideId}
              full
              role={modalBillsRole}
              fallback={modalBillsRole === 'sponsor' ? <SponsoredBills bioGuideID={bioGuideIdState} /> : null}
            />
          </div>
        ) : (
          <SponsoredBills bioGuideID={bioGuideIdState} />
        )}
      </Modal>

      <Modal id='fullBillModal' size="xl" aria-labelledby="contained-modal-title-vcenter" show={showModal2} onHide={() => setShowModal2(false)}>
        <Modal.Header id='billModalHeader' closeButton>
          <h3>{t('common:memberVotesTitle', '{{name}} Votes', { name })}</h3>
        </Modal.Header>
        {votesSource === 'api2' ? (
          <div className="member-modal-list">
            <MemberVotesList
              bioguideId={bioGuideId}
              full
              fallback={isSenator ? null : <APIMemberVotePullModal bio_guide_id={bioGuideId} />}
            />
          </div>
        ) : (
          <APIMemberVotePullModal bio_guide_id={bioGuideId} />
        )}
      </Modal>

      <Modal id='fullBillModal' size="xl" aria-labelledby="contained-modal-title-vcenter" show={showTradesModal} onHide={() => setShowTradesModal(false)}>
        <Modal.Header id='billModalHeader' closeButton>
          <h3>{t('common:memberTradesTitle', '{{name}} Stock Trades', { name })}</h3>
        </Modal.Header>
        <div className="member-modal-list">
          {recordData ? (
            <MemberTradesPanel bioguideId={bioGuideId} record={recordData} full />
          ) : (
            recordPending
          )}
        </div>
      </Modal>

      {/* Collection Modal for Member Profile */}
      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />

      <style>{`
        /* Match the Glance column width on the homepage so cards read as cards
           rather than full-width bars on wide desktop screens. */
        .member-page-content {
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          box-sizing: border-box;
        }

        /* Smaller portrait, so the name, contact and record lines lead. */
        .member-profile-image--desktop {
          width: 96px;
          height: 96px;
        }

        .member-profile-image--mobile {
          width: 56px;
          height: 56px;
        }

        .member-profile-container {
          align-items: flex-start;
        }

        .member-record-pending {
          display: flex;
          justify-content: center;
          padding: 2rem 0;
        }

        /* The api2 lists inside the View More modals: the same column as the
           page, with room around it. */
        .member-modal-list {
          max-width: 720px;
          width: 100%;
          margin: 0 auto;
          padding: 16px;
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
}

export default MemberDetailPage;